import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, 
  Users, 
  Clock, 
  AlertTriangle, 
  Target,
  ArrowRight,
  Calendar,
  RefreshCw,
  BarChart3,
  Filter,
  Award,
  Timer,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { usePermissions } from '../context/PermissionsContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = {
  primary: '#C9A227',
  secondary: '#1E3A5F',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#3B82F6',
  purple: '#8B5CF6'
};

const STAGE_COLORS = {
  cold_data: '#94A3B8',
  attempting_contact: '#64748B',
  contacted: '#3B82F6',
  qualified: '#8B5CF6',
  consultation_scheduled: '#F59E0B',
  consultation_completed: '#10B981',
  warm_followup: '#F97316',
  rsvp_office_event: '#EC4899',
  attended_office_event: '#14B8A6',
  closed_won: '#22C55E',
  closed_lost: '#EF4444'
};

const SEVERITY_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300'
};

export default function FunnelDashboardPage() {
  const { role } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [funnelData, setFunnelData] = useState(null);
  const [repPerformance, setRepPerformance] = useState(null);
  const [agingAlerts, setAgingAlerts] = useState(null);
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 90), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedRep, setSelectedRep] = useState('all');
  const [agingThreshold, setAgingThreshold] = useState(7);
  const [expandedRep, setExpandedRep] = useState(null);

  const canViewRepPerformance = ['admin', 'owner', 'sales_manager', 'top_management'].includes(role);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (selectedRep && selectedRep !== 'all') params.append('rep_id', selectedRep);

      const [funnelRes, agingRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/funnel?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/lead-aging?threshold_days=${agingThreshold}`, { credentials: 'include' })
      ]);

      if (funnelRes.ok) {
        setFunnelData(await funnelRes.json());
      }
      if (agingRes.ok) {
        setAgingAlerts(await agingRes.json());
      }

      // Fetch rep performance if authorized
      if (canViewRepPerformance) {
        const repRes = await fetch(`${API_URL}/api/analytics/rep-performance?${params}`, { credentials: 'include' });
        if (repRes.ok) {
          setRepPerformance(await repRes.json());
        }
      }
    } catch (err) {
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFrom, dateTo, selectedRep, agingThreshold, canViewRepPerformance]);

  useEffect(() => {
    fetchData();
  }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="funnel-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  const formatStageLabel = (stage) => {
    if (!stage) return '';
    return stage.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Prepare data for charts to avoid complex inline expressions
  const funnelSummary = funnelData?.funnel_summary || [];
  const stageDistribution = funnelData?.stage_distribution || {};
  const avgDaysData = Object.entries(funnelData?.avg_days_per_stage || {})
    .filter(([, days]) => days > 0)
    .map(([stage, days]) => ({ stage: formatStageLabel(stage), days }));
  const stageConversions = funnelData?.stage_conversions || [];
  const conversionsChartData = stageConversions.map(c => ({
    name: formatStageLabel(c.from_stage).substring(0, 10),
    rate: c.conversion_rate
  }));
  const keyMetrics = funnelData?.key_metrics || {};
  const totalLeads = funnelData?.total_leads_in_range || 1;
  const repsList = repPerformance?.reps || [];
  const repSummary = repPerformance?.summary || {};
  const repChartData = repsList.map(r => ({
    name: r.name.split(' ')[0],
    conversion: r.conversion_rate,
    tasks: r.task_completion_rate
  }));
  const alerts = agingAlerts?.alerts || [];
  const alertsSummary = agingAlerts?.summary || {};
  const agingByStage = alertsSummary.by_stage || {};
  const agingChartData = Object.entries(agingByStage).map(([stage, count]) => ({
    stage: formatStageLabel(stage),
    count
  }));

  return (
    <div className="space-y-6" data-testid="funnel-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-gold" />
            Funnel Intelligence
          </h1>
          <p className="text-slate-500 mt-1">Performance analytics and conversion insights</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs text-slate-500">From Date</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1"
                data-testid="date-from"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs text-slate-500">To Date</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1"
                data-testid="date-to"
              />
            </div>
            {canViewRepPerformance && repsList.length > 0 && (
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs text-slate-500">Filter by Rep</Label>
                <Select value={selectedRep} onValueChange={setSelectedRep}>
                  <SelectTrigger className="mt-1" data-testid="rep-filter">
                    <SelectValue placeholder="All Reps" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Reps</SelectItem>
                    {repsList.map((rep) => (
                      <SelectItem key={rep.rep_id} value={rep.rep_id}>
                        {rep.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={handleRefresh} className="bg-gold hover:bg-gold/90 text-slate-900">
              <Filter className="w-4 h-4 mr-2" />
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics Row */}
      {funnelData && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card data-testid="metric-win-rate">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Target className="w-8 h-8 text-emerald-500" />
                <span className="text-2xl font-bold text-emerald-600">
                  {keyMetrics.overall_win_rate || 0}%
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Overall Win Rate</p>
            </CardContent>
          </Card>

          <Card data-testid="metric-rsvp-attendance">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Users className="w-8 h-8 text-pink-500" />
                <span className="text-2xl font-bold text-pink-600">
                  {keyMetrics.rsvp_to_attendance || 0}%
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">RSVP → Attendance</p>
            </CardContent>
          </Card>

          <Card data-testid="metric-consultation-show">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Calendar className="w-8 h-8 text-amber-500" />
                <span className="text-2xl font-bold text-amber-600">
                  {keyMetrics.consultation_show_rate || 0}%
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Consultation Show Rate</p>
            </CardContent>
          </Card>

          <Card data-testid="metric-attendance-deposit">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <TrendingUp className="w-8 h-8 text-teal-500" />
                <span className="text-2xl font-bold text-teal-600">
                  {keyMetrics.attendance_to_deposit || 0}%
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Attendance → Deposit</p>
            </CardContent>
          </Card>

          <Card data-testid="metric-consultation-deposit">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Award className="w-8 h-8 text-purple-500" />
                <span className="text-2xl font-bold text-purple-600">
                  {keyMetrics.consultation_to_deposit || 0}%
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Consult → Deposit</p>
            </CardContent>
          </Card>

          <Card data-testid="metric-total-leads">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Users className="w-8 h-8 text-blue-500" />
                <span className="text-2xl font-bold text-blue-600">
                  {totalLeads || 0}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Total Leads</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="funnel" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="funnel" data-testid="tab-funnel">
            <TrendingUp className="w-4 h-4 mr-2" />
            Funnel
          </TabsTrigger>
          <TabsTrigger value="conversions" data-testid="tab-conversions">
            <Target className="w-4 h-4 mr-2" />
            Conversions
          </TabsTrigger>
          {canViewRepPerformance && (
            <TabsTrigger value="reps" data-testid="tab-reps">
              <Users className="w-4 h-4 mr-2" />
              Rep Performance
            </TabsTrigger>
          )}
          <TabsTrigger value="aging" data-testid="tab-aging">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Aging Alerts
            {agingAlerts?.summary?.total_aging_leads > 0 && (
              <Badge variant="destructive" className="ml-2">
                  {alertsSummary.total_aging_leads || 0}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Funnel Tab */}
        <TabsContent value="funnel" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Funnel Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Sales Funnel</CardTitle>
                <CardDescription>Lead progression through stages</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={funnelSummary}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="label" type="category" width={100} />
                      <Tooltip />
                      <Bar dataKey="count" name="Leads" fill="#C9A227" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Stage Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Stage Distribution</CardTitle>
                <CardDescription>Current lead count by stage</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stageDistribution)
                    .filter(([, count]) => count > 0)
                    .sort((a, b) => b[1] - a[1])
                    .map(([stage, count]) => (
                      <div key={stage} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: STAGE_COLORS[stage] || '#94A3B8' }}
                          />
                          <span className="text-sm">{formatStageLabel(stage)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-slate-100 rounded-full h-2">
                            <div
                              className="h-2 rounded-full"
                              style={{
                                width: `${Math.min((count / totalLeads) * 100, 100)}%`,
                                backgroundColor: STAGE_COLORS[stage] || '#94A3B8'
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Average Days Per Stage */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Average Days per Stage</CardTitle>
              <CardDescription>Time leads spend in each stage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={avgDaysData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="stage" angle={-45} textAnchor="end" height={100} interval={0} tick={{ fontSize: 11 }} />
                    <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="days" name="Avg Days" fill="#1E3A5F" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Conversions Tab */}
        <TabsContent value="conversions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Stage-to-Stage Conversion Rates</CardTitle>
              <CardDescription>Conversion rate between consecutive stages</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>From Stage</TableHead>
                    <TableHead className="text-center">→</TableHead>
                    <TableHead>To Stage</TableHead>
                    <TableHead className="text-right">From Count</TableHead>
                    <TableHead className="text-right">To Count</TableHead>
                    <TableHead className="text-right">Conversion Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stageConversions.map((conv) => (
                    <TableRow key={`${conv.from_stage}-${conv.to_stage}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: STAGE_COLORS[conv.from_stage] }}
                          />
                          {formatStageLabel(conv.from_stage)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <ArrowRight className="w-4 h-4 text-slate-400 mx-auto" />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: STAGE_COLORS[conv.to_stage] }}
                          />
                          {formatStageLabel(conv.to_stage)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{conv.from_count}</TableCell>
                      <TableCell className="text-right">{conv.to_count}</TableCell>
                      <TableCell className="text-right">
                        <Badge 
                          variant={conv.conversion_rate >= 50 ? 'default' : conv.conversion_rate >= 25 ? 'secondary' : 'destructive'}
                          className={conv.conversion_rate >= 50 ? 'bg-emerald-500' : ''}
                        >
                          {conv.conversion_rate}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Conversion Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Conversion Rate Visualization</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={conversionsChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 100]} label={{ value: '%', angle: -90, position: 'insideLeft' }} />
                    <Tooltip formatter={(value) => [`${value}%`, 'Conversion Rate']} />
                    <Bar dataKey="rate" fill={CHART_COLORS.primary} name="Conversion Rate" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rep Performance Tab */}
        {canViewRepPerformance && (
          <TabsContent value="reps" className="space-y-4">
            {/* Summary Cards */}
            {repSummary.total_reps > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-slate-900">{repSummary.total_reps}</div>
                    <p className="text-sm text-slate-500">Active Reps</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-emerald-600">{repSummary.avg_conversion_rate}%</div>
                    <p className="text-sm text-slate-500">Avg Conversion Rate</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-blue-600">{repSummary.total_leads}</div>
                    <p className="text-sm text-slate-500">Total Leads</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-gold">{repSummary.total_won}</div>
                    <p className="text-sm text-slate-500">Total Won</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Rep Performance Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Rep Performance Breakdown</CardTitle>
                <CardDescription>Individual performance metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rep</TableHead>
                      <TableHead className="text-right">Leads</TableHead>
                      <TableHead className="text-right">Won</TableHead>
                      <TableHead className="text-right">Lost</TableHead>
                      <TableHead className="text-right">Conv. Rate</TableHead>
                      <TableHead className="text-right">Tasks Done</TableHead>
                      <TableHead className="text-right">Task Rate</TableHead>
                      <TableHead className="text-right">Overdue</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {repsList.map((rep) => (
                      <React.Fragment key={rep.rep_id}>
                        <TableRow className="cursor-pointer hover:bg-slate-50" onClick={() => setExpandedRep(expandedRep === rep.rep_id ? null : rep.rep_id)}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-gold">
                                  {rep.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium">{rep.name}</div>
                                <div className="text-xs text-slate-500">{rep.role}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{rep.total_leads}</TableCell>
                          <TableCell className="text-right text-emerald-600 font-medium">{rep.closed_won}</TableCell>
                          <TableCell className="text-right text-red-600">{rep.closed_lost}</TableCell>
                          <TableCell className="text-right">
                            <Badge 
                              variant={rep.conversion_rate >= 30 ? 'default' : rep.conversion_rate >= 15 ? 'secondary' : 'destructive'}
                              className={rep.conversion_rate >= 30 ? 'bg-emerald-500' : ''}
                            >
                              {rep.conversion_rate}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{rep.completed_tasks}/{rep.total_tasks}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline">{rep.task_completion_rate}%</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {rep.overdue_tasks > 0 ? (
                              <Badge variant="destructive">{rep.overdue_tasks}</Badge>
                            ) : (
                              <span className="text-slate-400">0</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {expandedRep === rep.rep_id ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            )}
                          </TableCell>
                        </TableRow>
                        {expandedRep === rep.rep_id && (
                          <TableRow>
                            <TableCell colSpan={9} className="bg-slate-50 p-4">
                              <div className="text-sm font-medium mb-2">Stage Distribution</div>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(rep.stage_distribution || {}).map(([stage, count]) => (
                                  <Badge key={stage} variant="outline" style={{ borderColor: STAGE_COLORS[stage] }}>
                                    {formatStageLabel(stage)}: {count}
                                  </Badge>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Rep Comparison Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Conversion Rate Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={repChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="conversion" name="Conversion %" fill="#10B981" />
                      <Bar dataKey="tasks" name="Task Completion %" fill="#3B82F6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Aging Alerts Tab */}
        <TabsContent value="aging" className="space-y-4">
          {/* Threshold Control */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <Label className="text-sm">Alert Threshold:</Label>
                <Select value={String(agingThreshold)} onValueChange={(v) => setAgingThreshold(Number(v))}>
                  <SelectTrigger className="w-32" data-testid="aging-threshold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="5">5 days</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={handleRefresh}>
                  Update
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          {alertsSummary.total_aging_leads > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-red-600">{alertsSummary.critical || 0}</div>
                  <p className="text-sm text-red-700">Critical (2x threshold)</p>
                </CardContent>
              </Card>
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-orange-600">{alertsSummary.high || 0}</div>
                  <p className="text-sm text-orange-700">High (1.5x threshold)</p>
                </CardContent>
              </Card>
              <Card className="border-yellow-200 bg-yellow-50">
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-yellow-600">{alertsSummary.medium || 0}</div>
                  <p className="text-sm text-yellow-700">Medium (at threshold)</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-slate-900">{alertsSummary.total_aging_leads || 0}</div>
                  <p className="text-sm text-slate-500">Total Aging Leads</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Aging Leads Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Timer className="w-5 h-5 text-amber-500" />
                Leads Requiring Attention
              </CardTitle>
              <CardDescription>Leads that have been inactive beyond their stage threshold</CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No aging leads found</p>
                  <p className="text-sm">All leads are being actively worked</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Severity</TableHead>
                      <TableHead>Lead</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead className="text-right">Days in Stage</TableHead>
                      <TableHead className="text-right">Threshold</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Contact</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.slice(0, 50).map((alert) => (
                      <TableRow key={alert.lead_id}>
                        <TableCell>
                          <Badge className={SEVERITY_COLORS[alert.severity]}>
                            {alert.severity.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{alert.lead_name || 'Unknown'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: STAGE_COLORS[alert.stage] }}
                            />
                            {formatStageLabel(alert.stage)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={alert.days_in_stage >= alert.threshold_days * 2 ? 'text-red-600 font-bold' : ''}>
                            {alert.days_in_stage} days
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-slate-500">{alert.threshold_days} days</TableCell>
                        <TableCell>{alert.assigned_to_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {alert.phone && (
                              <a href={`tel:${alert.phone}`} className="text-slate-400 hover:text-slate-600">
                                <Phone className="w-4 h-4" />
                              </a>
                            )}
                            {alert.email && (
                              <a href={`mailto:${alert.email}`} className="text-slate-400 hover:text-slate-600">
                                <Mail className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Aging by Stage Chart */}
          {Object.keys(agingByStage).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Aging Leads by Stage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={agingChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="stage" angle={-45} textAnchor="end" height={80} interval={0} tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" name="Aging Leads" fill="#F59E0B" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
